/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef } from "react";
import { observer } from "mobx-react";
import CheckBox from "Components/common/CheckBox";
import Button from "Components/common/Button";
import SearchInputBox from "Components/common/SearchInputBox";
import { dataSearchState } from "../state";

const DropDownMenu = props => {
  const {
    id: dropdownId = "",
    className = "",
    filteredList = [],
    setFilteredList = () => { },
    options = [],
    handleSelectActionCbk = "",
    handleSubmenuSelectActionCbk = "",
    itemWithSubmenu,
    hasSubMenu,
    hasSubMenuAccordion = false,
    fromSearch = false,
    showDropdown = false
  } = props || {};

  const [openSubmenuIndexes, setOpenSubmenuIndexes] = useState([]);
  const prevShowDropdownRef = useRef(false);
  const prevFromSearchRef = useRef(false);

  // When dropdown opens, auto-expand only categories that have checked sub-items (once).
  useEffect(() => {
    const justOpened = showDropdown && !prevShowDropdownRef.current;
    prevShowDropdownRef.current = !!showDropdown;
    if (!justOpened || !filteredList?.length || !hasSubMenuAccordion) return;
    const indexesToOpen = [];
    filteredList.forEach((item, index) => {
      const submenu = item?.submenu || [];
      if (submenu.length && submenu.some(s => s?.checked)) {
        indexesToOpen.push(index);
      }
    });
    if (indexesToOpen.length > 0) {
      setOpenSubmenuIndexes(indexesToOpen);
    }
  }, [showDropdown, filteredList, hasSubMenuAccordion]);

  // When user searches: expand only categories that have visible (matching) sub-items, so they can be closed.
  // When search is cleared: collapse to only categories with checked sub-items.
  // Use the same filtered list we render (displayList when fromSearch) so indexes match.
  useEffect(() => {
    if (!hasSubMenuAccordion || !filteredList?.length) return;
    if (fromSearch) {
      const list = filteredList.filter(
        i =>
          i?.show !== false &&
          (i?.submenu || []).some(s => s?.show !== false)
      );
      const indexesWithVisible = [];
      list.forEach((item, index) => {
        const submenu = item?.submenu || [];
        if (submenu.length && submenu.some(s => s?.show !== false)) {
          indexesWithVisible.push(index);
        }
      });
      setOpenSubmenuIndexes(indexesWithVisible);
      prevFromSearchRef.current = true;
    } else {
      if (prevFromSearchRef.current) {
        const indexesWithChecked = [];
        filteredList.forEach((item, index) => {
          const submenu = item?.submenu || [];
          if (submenu.length && submenu.some(s => s?.checked)) {
            indexesWithChecked.push(index);
          }
        });
        setOpenSubmenuIndexes(indexesWithChecked);
        prevFromSearchRef.current = false;
      }
    }
  }, [fromSearch, filteredList, hasSubMenuAccordion]);

  const handleSelectAction = (checked = "", uId = "", name = "", item) => {
    if (handleSubmenuSelectActionCbk && hasSubMenu) {
      handleSubmenuSelectActionCbk(checked, uId, name, item);
    } else if (handleSelectActionCbk) {
      handleSelectActionCbk(checked, uId, name);
    } else {
      let tempData = [...options];
      tempData = tempData.map(item => {
        const { id = "" } = item || {};
        if (id === uId) {
          item.checked = checked;
        }
        return item;
      });
      setFilteredList(tempData);

      if (dropdownId !== "team") {
        const updatedFilteredList = tempData
          ?.map(item => {
            if (item.checked) {
              return item?.id;
            }
          })
          ?.filter(Boolean);
        dataSearchState.setFilterComponentData({
          ...dataSearchState.filterComponentData,
          [dropdownId]: updatedFilteredList
        });
      }
    }
  };

  const handleOpenCloseAcc = index => {
    const temp = [...openSubmenuIndexes];
    if (openSubmenuIndexes?.includes(index)) {
      const submenuIndex = openSubmenuIndexes.indexOf(index);
      temp.splice(submenuIndex, 1);
    } else {
      temp.push(index);
    }
    setOpenSubmenuIndexes(temp);
  };

  const displayList =
    fromSearch && filteredList?.length > 0
      ? filteredList.filter(
          i =>
            i?.show !== false &&
            (i?.submenu || []).some(s => s?.show !== false)
        )
      : filteredList || [];

  return (
    <div className={`dropdownList ${className}`}>
      {displayList?.length > 0 &&
        displayList?.map((item, index) => {
          const {
            id = "",
            value = "",
            checked = false,
            indeterminate = false,
            show = false,
            showSubmenu = false,
            submenu = [],
            dotColor = ""
          } = item || {};

          if (typeof show === "string" || show) {
            const visibleSubmenu = fromSearch
              ? (submenu || []).filter(s => s?.show !== false)
              : (submenu || []);
            return (
              <div className="dropdownItems" key={id + index}>
                <div
                  className={`flexCenter ${id === "selectAll" ? "selectAll" : ""
                    }`}
                >
                  {hasSubMenuAccordion && id !== "selectAll" && (
                    <i
                      className="material-icons rightArrowIcon"
                      onClick={() => handleOpenCloseAcc(index)}
                    >
                      {openSubmenuIndexes?.includes(index)
                        ? "keyboard_arrow_down"
                        : "keyboard_arrow_right"}
                    </i>
                  )}
                  <CheckBox
                    name={value}
                    id={id}
                    dotColor={dotColor}
                    checked={checked}
                    indeterminate={indeterminate}
                    cbk={handleSelectAction}
                    hasSubMenu={hasSubMenu}
                    itemWithSubmenu={itemWithSubmenu}
                  />
                </div>
                {showSubmenu &&
                  openSubmenuIndexes?.includes(index) && (
                    <DropDownMenu
                      id={dropdownId}
                      className="subMenu"
                      filteredList={visibleSubmenu}
                      options={visibleSubmenu}
                      handleSubmenuSelectActionCbk={
                        handleSubmenuSelectActionCbk
                      }
                      hasSubMenu={true}
                      itemWithSubmenu={item}
                      fromSearch={fromSearch}
                    />
                  )}
              </div>
            );
          }
        })}
    </div>
  );
};

const MultiSelectDropdownList = observer(props => {
  const {
    options = [],
    id = "",
    className = "",
    dropdownPositionStyle = {},
    showDropdown: showDropdownMenu = false,
    showSearchPanel = true,
    handleSelectActionCbk,
    handleSubmenuSelectActionCbk,
    setTableLoading = () => { },
    handleApplySelectionCbk,
    searchFilterCbk,
    hasSubMenuAccordion = false,
    setActiveIndex = () => { },
    resetDropdownToInitialState = () => { },
    resetFilterOptions = () => { },
    discoverPage = false,
    onCloseDropdownOnly = null
  } = props || {};

  const searchBoxRef = useRef(null);
  const [filteredList, setFilteredList] = useState(options);
  const [showDropdown, setShowDropdown] = useState(showDropdownMenu);
  const [clearSearchInput, setClearSearchInput] = useState(false);
  const [fromSearch, setFromSearch] = useState(false);

  const searchFilterList = (value = "") => {
    if (searchFilterCbk) {
      setFromSearch(true);
      searchFilterCbk(value);
    } else {
      const lowercasedValue = value?.toLowerCase() || "";
      if (!lowercasedValue) {
        let temp = options?.map(item => {
          item.show = true;
          return item;
        });
        setFilteredList(temp);
      } else {
        let count = 0;
        const filteredData =
          options?.length > 0 &&
          options?.filter(item => {
            let val = item?.value
              ?.toString()
              ?.toLowerCase()
              ?.includes(lowercasedValue);
            val === false && count++;
            item["show"] = val;
            return item;
          });
        setFilteredList(filteredData);
      }
    }
  };

  const clearSearchCbk = () => {
    setClearSearchInput(true);
    setFilteredList(options);
    setFromSearch(false);
  };

  const handleApplySelectionButton = () => {
    setShowDropdown(false);
    setActiveIndex("");
    if (handleApplySelectionCbk) {
      handleApplySelectionCbk();
    }
    setFromSearch(false);
    resetDropdownToInitialState();
  };

  const resetState = () => {
    // Funding and department use accordion submenus; store holds submenu IDs, not category IDs. Keep list in sync with parent options.
    if (id === "funding" || id === "department") {
      setFilteredList([...options]);
    } else {
      const temp = [...filteredList];
      temp.map(item => {
        if (id !== "team") {
          item.checked = dataSearchState.filterComponentData?.[id]?.includes(
            item?.id
          );
        }
        return item;
      });
      setFilteredList([...temp]);
    }
    setShowDropdown(false);
    setClearSearchInput(true);
    setActiveIndex("");
    resetDropdownToInitialState();
    resetFilterOptions();
    setFromSearch(false);
  };

  const resetSelect = () => {
    // Funding and department: store holds submenu item IDs; sync from parent options (already synced from store).
    if (id === "funding" || id === "department") {
      setFilteredList([...options]);
      return;
    }
    const temp = [...filteredList];
    temp.map(item => {
      if (id !== "team") {
        item.checked = dataSearchState.filterComponentData?.[id]?.includes(
          item?.id
        );
      }
      return item;
    });
    setFilteredList([...temp]);
  };

  useEffect(() => {
    if (id !== "team" && dataSearchState.clearAll) {
      resetState();
      dataSearchState.setClearAll(false);
    }
  }, [dataSearchState.clearAll]);

  useEffect(() => {
    if (discoverPage) {
      resetSelect();
    }
  }, [dataSearchState.filterComponentData]);

  useEffect(() => {
    if (options?.length > 0 && ["industry", "team", "department", "funding"].includes(id)) {
      setFilteredList([...options]);
    }
  }, [options]);

  // When filter id or options change in discover (e.g. switch Revenue ↔ head count), show correct options.
  // Skip for department/funding: they use accordion submenus and options already carry checked state from the parent.
  useEffect(() => {
    if (!discoverPage || !id || id === "department" || id === "funding") return;
    const list = (options || []).map(opt => ({
      ...opt,
      checked: dataSearchState.filterComponentData?.[id]?.includes(opt?.id) ?? false
    }));
    setFilteredList(list);
  }, [id, options, discoverPage]);

  useEffect(() => {
    setShowDropdown(showDropdownMenu);
  }, [showDropdownMenu]);

  useEffect(() => {
    // Removed scrollIntoView which causes sudden page jumps
  }, [showDropdown]);

  useEffect(() => {
    setTableLoading(dataSearchState?.discoverTableLoading);
  }, [dataSearchState?.discoverTableLoading]);

  return (
    showDropdown && (
      <>
        <div
          className={`dropdownListWrapper ${className}`}
          style={dropdownPositionStyle}
          id="dropdownListWrapper"
        >
          {showSearchPanel && (
            <SearchInputBox
              valueCbk={searchFilterList}
              type="text"
              placeholder="Search"
              searchBoxRef={searchBoxRef}
              clearSearchInput={clearSearchInput}
              clearSearchCbk={clearSearchCbk}
            />
          )}
          <DropDownMenu
            id={id}
            options={options}
            filteredList={filteredList}
            setFilteredList={setFilteredList}
            handleSelectActionCbk={handleSelectActionCbk}
            handleSubmenuSelectActionCbk={handleSubmenuSelectActionCbk}
            hasSubMenuAccordion={hasSubMenuAccordion}
            fromSearch={fromSearch}
            showDropdown={showDropdownMenu}
          />
          {onCloseDropdownOnly && (
            <button
              type="button"
              className="dropdownDoneBtn"
              onClick={() => {
                onCloseDropdownOnly();
                setShowDropdown(false);
              }}
            >
              Done
            </button>
          )}
          {false && <Button
            id="applySelection"
            name="applySelection"
            type="button"
            className="applySelectionBtn"
            btnText="Apply"
            icon={false}
            loader={false}
            click={handleApplySelectionButton}
          />}
        </div>
        {id !== "team" && !discoverPage && (
          <div
            className="dropdownOverlay"
            onClick={() => {
              if (onCloseDropdownOnly) {
                onCloseDropdownOnly();
                setShowDropdown(false);
              } else {
                resetState();
              }
            }}
          />
        )}
      </>
    )
  );
});

const CustomMultiSelect = observer(props => {
  return <MultiSelectDropdownList {...props} />;
});

export { CustomMultiSelect };
export default CustomMultiSelect;
